<script setup lang="ts">
import { ChipComponent, NoteComponent, ProfileOptionItem } from '@metapic-apps/shared'
import { computed, ref, watch } from 'vue'

import { tabs as defaultTabs, targetInputPlaceholder, useTargetMatchUsers, useTargetOptions } from './TargetInput.helpers'

import CheckIcon from '@/assets/icons/check.svg?component'
import type { CampaignFormType } from '@/lib/types/campaign.types'
import { TargetItemEnum, type TargetItemType } from '@/lib/types/common.types'
import { BoxComponent, FlexComponent, InputComponent, LoadingComponent, TitleComponent, TypographyComponent } from '@/ui-library'

const props = defineProps<{ title?: string; isEditMode?: boolean; name: string; visibleTabs?: TargetItemEnum[] }>()

const advertiserGroup = defineModel<CampaignFormType['advertiserGroup']>('advertiserGroup', { required: true })
const targets = defineModel<CampaignFormType['targets']>('targets', { required: true })
const hasUnmatchedTargets = defineModel<CampaignFormType['hasUnmatchedTargets']>('hasUnmatchedTargets')
const unmatchedTargets = ref<string[]>([])

const inputValue = ref('')
const searchQuery = ref('')
const matchUsersQuery = ref<string[]>([])
const tabs = computed(() => defaultTabs.filter((tab) => !props.visibleTabs || props.visibleTabs.includes(tab.key)))
const activeTab = ref<Exclude<TargetItemEnum, TargetItemEnum.RevenueTier>>(TargetItemEnum.User)

const { options, isLoading, users } = useTargetOptions(activeTab, advertiserGroup, searchQuery, props.visibleTabs)
const usersById = computed(() => new Map(users.value.map((user) => [user.id, user])))
const getUser = (id: TargetItemType['id']) =>
  activeTab.value === TargetItemEnum.User && typeof id === 'number' ? usersById.value.get(id) : undefined
const { data: matchUsersData, isLoading: isMatchUsersLoading } = useTargetMatchUsers(advertiserGroup, matchUsersQuery)

const handleClose = (tag: TargetItemType) => (targets.value = targets.value.filter((item) => item.id !== tag.id))

const handleUnmatchedTagClose = (tag: string) => (unmatchedTargets.value = unmatchedTargets.value.filter((item) => item !== tag))

const toggleTarget = (item: TargetItemType) => {
  const isTargetPresent = targets.value.some((target) => target.id === item.id)
  if (isTargetPresent) {
    targets.value = targets.value.filter((target) => target.id !== item.id)
  } else {
    targets.value.push(item)
  }
}

watch(
  () => inputValue.value,
  (newValue) => {
    if (activeTab.value === TargetItemEnum.User) {
      const users = newValue.split(/[\s,;\t\n]+/).filter((item) => Boolean(item))
      if (users.length > 1) {
        matchUsersQuery.value = users
        searchQuery.value = ''
      } else {
        searchQuery.value = newValue
        matchUsersQuery.value = []
      }
    } else {
      searchQuery.value = newValue
      matchUsersQuery.value = []
    }
  },
)

watch(
  () => advertiserGroup.value,
  () => {
    searchQuery.value = ''
    matchUsersQuery.value = []
    inputValue.value = ''
    unmatchedTargets.value = []
  },
)

watch(
  () => isMatchUsersLoading.value,
  (newValue) => {
    if (!newValue) {
      inputValue.value = ''
    }
  },
)

watch(
  () => matchUsersData.value,
  (newMatchUsersData) => {
    if (newMatchUsersData) {
      const newMatchedTargets = newMatchUsersData.matched.filter((user) => !targets.value.some((target) => target.id === user.id))
      targets.value.push(...newMatchedTargets)
      const newUnmatchedTargets = newMatchUsersData.unmatched.filter((user) => !unmatchedTargets.value.includes(user))
      unmatchedTargets.value.push(...newUnmatchedTargets)
    }
  },
)

watch(
  () => unmatchedTargets.value,
  () => (hasUnmatchedTargets.value = unmatchedTargets.value.length > 0),
  { deep: true },
)
</script>

<template>
  <div>
    <el-form-item :prop="name">
      <BoxComponent v-if="title" :mb="16">
        <TitleComponent :title="title" />
      </BoxComponent>
      <FlexComponent class="campaign-form-target-input__tabs">
        <BoxComponent
          v-for="item in tabs"
          :key="item.key"
          :class="{ 'campaign-form-target-input__tab_active': activeTab === item.key }"
          :pt="8"
          :pb="8"
          :pr="16"
          :pl="16"
          class="campaign-form-target-input__tab"
          @click="
            () => {
              activeTab = item.key
              inputValue = ''
              searchQuery = ''
            }
          "
        >
          <TypographyComponent variant="label-large">
            {{ item.title }}
          </TypographyComponent>
        </BoxComponent>
      </FlexComponent>
      <el-dropdown trigger="click" class="select" :name="name" placement="bottom-start">
        <BoxComponent :min-w="140">
          <InputComponent v-model="inputValue" :placeholder="targetInputPlaceholder[activeTab]" />
        </BoxComponent>
        <template #dropdown>
          <div class="select__scrollable-content">
            <BoxComponent v-if="isLoading" :p="16">
              <LoadingComponent />
            </BoxComponent>
            <template v-else>
              <template v-if="options.length">
                <div
                  v-for="(option, index) in options"
                  :key="`${option.id} + ${index}`"
                  :class="['select__item', { select__item_flush: getUser(option.id) }]"
                  @click="
                    () => {
                      toggleTarget(option)
                    }
                  "
                >
                  <ProfileOptionItem
                    v-if="getUser(option.id)"
                    :id="getUser(option.id)!.id"
                    :username="getUser(option.id)!.username"
                    :email="getUser(option.id)!.email"
                    :image="getUser(option.id)!.image"
                    :avatar-name="getUser(option.id)!.displayName"
                    :added="targets.some((item) => item.id === option.id)"
                    indicator="check"
                  />
                  <FlexComponent v-else justify="space-between" align="center" :gap="8">
                    <TypographyComponent variant="body-large" color="'var(--primary)'">
                      <template v-if="option.type !== TargetItemEnum.Segment">{{ option.id }} </template>{{ option.label }}
                    </TypographyComponent>
                    <el-icon v-if="targets.some((item) => item.id === option.id)" :size="16">
                      <CheckIcon />
                    </el-icon>
                  </FlexComponent>
                </div>
              </template>
              <BoxComponent v-else :pl="16" :pb="16" :mt="16">
                <TypographyComponent is="p" variant="body-medium" text-align="center">No matching data</TypographyComponent>
              </BoxComponent>
            </template>
          </div>
        </template>
      </el-dropdown>
      <FlexComponent
        v-if="targets.length || unmatchedTargets.length > 0"
        wrap="wrap"
        :gap="8"
        class="campaign-form-target-input__targets-value-container"
      >
        <ChipComponent v-for="item in unmatchedTargets" :key="item" variant="danger" closable @close="() => handleUnmatchedTagClose(item)">
          {{ item }}
        </ChipComponent>
        <ChipComponent v-for="item in targets" :key="item.id" closable @close="() => handleClose(item)">
          {{ item.label }}
        </ChipComponent>
      </FlexComponent>
      <BoxComponent v-if="unmatchedTargets.length" :mt="4">
        <TypographyComponent variant="body-small" color="var(--error)">
          Couldn't find {{ unmatchedTargets.length }} creator{{ unmatchedTargets.length > 1 ? 's' : '' }}.
        </TypographyComponent>
      </BoxComponent>
      <BoxComponent v-if="isEditMode" :mt="16">
        <NoteComponent> Removing creators will result in losing all statistics about already shared links. </NoteComponent>
      </BoxComponent>
    </el-form-item>
  </div>
</template>
<style lang="scss" scoped>
.campaign-form-target-input__targets-value-container {
  max-height: 134px;
  overflow: auto;
  padding: 16px 8px 8px;
  margin-top: -8px;
  margin-right: -1px;
  border-radius: 0 0 8px 8px;
  background: var(--inverted);
  box-shadow: 0 0 0 1px var(--secondary-12) inset;
}
.campaign-form-target-input__tabs {
  bottom: -1px;
  position: relative;
  background: var(--inverted);
}
.campaign-form-target-input__tab {
  cursor: pointer;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}
.campaign-form-target-input__tab_active {
  box-shadow: 0 0 0 1px var(--secondary-12) inset;
}

.select {
  width: 100%;
}

:deep(.el-tooltip__trigger) {
  width: 100%;
}

:deep(.el-input__wrapper) {
  border-radius: 0 8px 8px 8px;
  margin-left: 1px;
}

.select__scrollable-content {
  width: 590px;
  max-height: 364px;
  overflow: auto;
}

.select__item {
  padding: 16px;
  cursor: pointer;
}
.select__item_flush {
  padding: 0;
}
</style>
